"use client";

import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Trash2, Upload } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import queryKeys from "@/app/query-keys";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth";
import avatarUrl from "@/lib/image-urls";
import { trpcClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import LoadingSpinner from "../loading-spinner";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

const preloadImage = (url: string) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = reject;
        img.src = url;
    });
};

export function AvatarUpload() {
    const [imageUrl, setImageUrl] = useState<string | null>();
    const { data: session } = authClient.useSession();
    const [isUploading, setIsUploading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (session?.user.image) {
            const url = avatarUrl(session?.user.image, "original");
            setImageUrl(url);
        }
    }, [session?.user.image]);

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        // Add a temp preview URL
        const previewUrl = URL.createObjectURL(file);
        setImageUrl(previewUrl);

        // Upload file to server
        const { url, id } = await trpcClient.upload.getPresignedAvatarUrl.query({ contentType: file.type });

        await axios.put(url, file, {
            headers: {
                "Content-Type": file.type,
            },
        });

        const imageUrl = await trpcClient.user.updateImage.mutate({ id, contentType: file.type });

        const finalUrl = avatarUrl(imageUrl, "original");

        // preload the image with axios as a blob
        await preloadImage(finalUrl);
        setImageUrl(finalUrl);

        setIsUploading(false);

        queryClient.invalidateQueries({ queryKey: ["session"] });
    };

    const handleRemove = async () => {
        setIsRemoving(true);
        setImageUrl(null);

        await trpcClient.user.removeImage.mutate();

        setIsRemoving(false);
    };

    if (!session) return null;

    return (
        <div className="flex flex-row items-center gap-4">
            <div>
                <Avatar className="size-28 relative">
                    <AvatarImage className={cn("object-cover", isUploading && "opacity-45")} src={imageUrl ?? undefined} />
                    <AvatarFallback className="">No image</AvatarFallback>
                    {isUploading && <LoadingSpinner size="sm" className="text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />}
                </Avatar>
            </div>

            <div className="flex flex-row gap-2">
                <div className="relative">
                    <Button size="sm" variant="outline" disabled={isUploading} className="flex gap-1">
                        <Upload className="h-4 w-4" />
                        {isUploading ? "Uploading..." : "Upload"}
                    </Button>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleUpload}
                        disabled={isUploading}
                        className="absolute inset-0 cursor-pointer opacity-0"
                    />
                </div>

                <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRemove}
                    disabled={!imageUrl || isRemoving}
                    className="flex gap-1 text-destructive hover:text-destructive"
                >
                    <Trash2 className="h-4 w-4" />
                    {isRemoving ? "Removing..." : "Remove"}
                </Button>
            </div>
        </div>
    );
}
